import React, { useEffect, useState } from 'react';
import { Trash2, GripVertical, Plus, Eye, Loader2, ImageIcon } from 'lucide-react';
import { adminListImages, adminAddImage, adminDeleteImage, adminReorderImages } from '../../services/cms';
import { useToast } from '../../hooks/use-toast';

const ImagePanel = ({ episode }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ image_url: '', texte_alternatif: '', largeur: '', hauteur: '' });
  const [dragIdx, setDragIdx] = useState(null);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try { setImages(await adminListImages(episode.id)); } catch (e) { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [episode.id]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.image_url.trim()) return;
    try {
      await adminAddImage(episode.id, {
        image_url: form.image_url.trim(), texte_alternatif: form.texte_alternatif,
        largeur: parseInt(form.largeur) || 0, hauteur: parseInt(form.hauteur) || 0,
      });
      setForm({ image_url: '', texte_alternatif: '', largeur: '', hauteur: '' });
      await load();
    } catch (err) { toast({ title: 'Erreur', description: 'Impossible d’ajouter l’image.' }); }
  };

  const remove = async (id) => { await adminDeleteImage(id); await load(); };

  const commit = async (arr) => { setImages(arr); await adminReorderImages(episode.id, arr.map((x) => x.id)); };
  const onDrop = async (index) => {
    if (dragIdx === null || dragIdx === index) { setDragIdx(null); return; }
    const arr = [...images]; const [m] = arr.splice(dragIdx, 1); arr.splice(index, 0, m);
    setDragIdx(null); await commit(arr);
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-display font-700 text-lg">Images de lecture — {episode.titre}</h3>
        <a href={`/apercu/${episode.slug}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary text-secondary-foreground font-800 text-sm hover:scale-105 transition-transform"><Eye className="w-4 h-4" /> Aperçu du lecteur</a>
      </div>

      <form onSubmit={add} className="bg-muted rounded-2xl p-4 grid sm:grid-cols-[1fr_auto] gap-3 items-end">
        <div className="grid sm:grid-cols-4 gap-3">
          <div className="sm:col-span-2"><label className="text-xs font-800 mb-1 block">URL de l’image hébergée *</label><input value={form.image_url} onChange={upd('image_url')} className="w-full px-3 py-2 rounded-lg bg-card outline-none focus:ring-2 focus:ring-primary/40 font-600 text-sm" placeholder="https://..." /></div>
          <div><label className="text-xs font-800 mb-1 block">Largeur</label><input value={form.largeur} onChange={upd('largeur')} className="w-full px-3 py-2 rounded-lg bg-card outline-none font-600 text-sm" placeholder="800" /></div>
          <div><label className="text-xs font-800 mb-1 block">Hauteur</label><input value={form.hauteur} onChange={upd('hauteur')} className="w-full px-3 py-2 rounded-lg bg-card outline-none font-600 text-sm" placeholder="1400" /></div>
          <div className="sm:col-span-4"><label className="text-xs font-800 mb-1 block">Texte alternatif</label><input value={form.texte_alternatif} onChange={upd('texte_alternatif')} className="w-full px-3 py-2 rounded-lg bg-card outline-none font-600 text-sm" placeholder="Description de l’image" /></div>
        </div>
        <button type="submit" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white font-800 text-sm hover:scale-105 transition-transform h-fit"><Plus className="w-4 h-4" /> Ajouter</button>
      </form>

      {loading ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      : images.length === 0 ? (
        <div className="py-10 text-center text-muted-foreground"><ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-50" /><p className="font-600">Aucune image. Ajoutez des URLs ci-dessus.</p></div>
      ) : (
        <div className="space-y-2">
          <p className="text-sm font-700 text-muted-foreground">{images.length} image(s) — glissez pour réordonner</p>
          {images.map((img, i) => (
            <div key={img.id} draggable onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
              className={`flex items-center gap-3 bg-card border rounded-2xl p-2 transition-all ${dragIdx === i ? 'opacity-40 border-primary' : 'border-border'}`}>
              <GripVertical className="w-5 h-5 text-muted-foreground cursor-grab shrink-0" />
              <span className="w-7 h-7 rounded-full bg-primary text-white text-xs font-800 flex items-center justify-center shrink-0">{i + 1}</span>
              <img src={img.image_url} alt={img.texte_alternatif} className="w-14 h-14 object-cover rounded-lg" />
              <div className="flex-1 min-w-0"><p className="text-xs text-muted-foreground truncate">{img.image_url}</p>{(img.largeur || img.hauteur) ? <p className="text-[11px] text-muted-foreground">{img.largeur}×{img.hauteur}</p> : null}</div>
              <button onClick={() => remove(img.id)} className="w-9 h-9 rounded-full bg-muted flex items-center justify-center hover:bg-destructive hover:text-white transition-colors shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImagePanel;
